import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATS = ["coffee", "non-coffee", "food", "seasonal"];
const empty = { category: "coffee", name_id: "", name_en: "", description_id: "", description_en: "", price: 0, image_url: "", tags: [], available: true };

export default function MenuManagement() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/menu").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...it, tags: it.tags || [] }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: Number(form.price), tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
    if (editing) await api.put(`/menu/${editing.id}`, payload);
    else await api.post("/menu", payload);
    toast.success("Saved");
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/menu/${id}`); toast.success("Deleted"); load();
  };

  const uploadImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
    toast.success("Uploaded");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Menu Management</h1>
        <Button onClick={startNew} data-testid="menu-new-btn"><Plus size={14} className="mr-2" />New Item</Button>
      </div>
      <Card className="border-zinc-200">
        <CardContent className="p-0">
          <Table data-testid="menu-mgmt-table">
            <TableHeader><TableRow>
              <TableHead>Image</TableHead><TableHead>Name (ID)</TableHead><TableHead>Name (EN)</TableHead>
              <TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Available</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`menu-row-${it.id}`}>
                  <TableCell><div className="w-10 h-10 rounded bg-zinc-100 overflow-hidden">{it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}</div></TableCell>
                  <TableCell>{it.name_id}</TableCell>
                  <TableCell>{it.name_en}</TableCell>
                  <TableCell className="capitalize">{it.category}</TableCell>
                  <TableCell className="font-mono">{formatIDR(it.price)}</TableCell>
                  <TableCell>{it.available ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)} data-testid={`menu-edit-${it.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(it.id)} data-testid={`menu-del-${it.id}`}><Trash size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name (ID)</Label><Input value={form.name_id} onChange={(e) => setForm({ ...form, name_id: e.target.value })} data-testid="menu-form-name-id" /></div>
              <div><Label>Name (EN)</Label><Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="menu-form-name-en" /></div>
            </div>
            <div><Label>Description (ID)</Label><Textarea value={form.description_id} onChange={(e) => setForm({ ...form, description_id: e.target.value })} data-testid="menu-form-desc-id" /></div>
            <div><Label>Description (EN)</Label><Textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="menu-form-desc-en" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Price (IDR)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="menu-form-price" /></div>
              <div className="flex flex-col"><Label>Available</Label><Switch className="mt-2" checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} /></div>
            </div>
            <div><Label>Image URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="menu-form-image-url" /></div>
            <div><Label>Or upload</Label><Input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} data-testid="menu-form-upload" /></div>
            <div><Label>Tags (comma separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="menu-form-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
